/** @jsxImportSource @emotion/react */
import { forwardRef, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import Icon from '../Icon';
import useWindowSize from '../../hooks/useWindowSize';

import * as styles from './Accordion.style';

export type AccordionSizeType = 'medium' | 'large';

export interface AccordionProps {
  title: ReactNode;
  titleIcon?: string;
  expandIcon?: string;
  defaultExpended?: boolean;
  hasBorder?: boolean;
  size?: AccordionSizeType;
  onClick?: (expanded: boolean) => void;
  children: ReactNode;
  className?: string;
}

const Accordion = forwardRef<HTMLDivElement, AccordionProps>(function Accordion(
  {
    title,
    titleIcon,
    expandIcon = 'ic_arrowdown_md_gray_300',
    defaultExpended = false,
    hasBorder = true,
    size = 'large',
    onClick,
    children,
    className = '',
  },
  ref
) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState<boolean>(defaultExpended);
  const [height, setHeight] = useState<number>(0);
  const [screenWidth] = useWindowSize();

  const handleClick = useCallback(() => {
    const newState = !expanded;

    setExpanded(newState);

    onClick && onClick(newState);
  }, [expanded, onClick]);

  useEffect(() => {
    if (contentRef && contentRef.current) {
      const { height } = contentRef.current.getBoundingClientRect();

      setHeight(height);
    }
  }, [contentRef, screenWidth, children]);

  return (
    <div ref={ref} className={className} css={styles.container}>
      <div
        className="mrt-accordion-title"
        css={[styles.titleWrapper(size), !hasBorder && styles.noBorder]}
        onClick={handleClick}
      >
        <div css={styles.titleIconWrapper}>
          {titleIcon && <Icon css={styles.titleIcon} name={titleIcon} />}
          <div css={styles.title}>{title}</div>
        </div>
        <Icon css={[styles.expandIcon, expanded && styles.rotateIcon]} name={expandIcon} />
      </div>
      <div ref={contentRef} css={styles.hiddenContent(size)}>
        {children}
      </div>
      <div
        className="mrt-accordion-content"
        css={[styles.content(size), expanded && styles.showContent(height)]}
      >
        {children}
      </div>
    </div>
  );
});

export default Accordion;
